/**
 * A conservative, browser-context ownership lease for a persisted match.
 *
 * This port deliberately does not wrap or replace the IndexedDB CAS boundary. It only makes an
 * open second context read-only before it can offer a mutation to that boundary. Consumers must
 * still treat a storage conflict as authoritative.
 */

export type MatchOwnershipReason =
  "available" | "owned-by-another" | "taken-over" | "unavailable" | "uncertain" | "disposed";

export type MatchOwnershipSnapshot = Readonly<{
  kind: "owner" | "read-only";
  reason: MatchOwnershipReason;
  ownerId: string | null;
}>;

export type MatchOwnershipPort = Readonly<{
  getSnapshot(): MatchOwnershipSnapshot;
  subscribe(listener: (snapshot: MatchOwnershipSnapshot) => void): () => void;
  acquire(): Promise<MatchOwnershipSnapshot>;
  takeOver(): Promise<MatchOwnershipSnapshot>;
  release(): Promise<MatchOwnershipSnapshot>;
  dispose(): Promise<void>;
}>;

type WebLock = object;

export type WebLockManager = Readonly<{
  request(
    name: string,
    options: Readonly<{ mode: "exclusive"; ifAvailable: true }>,
    callback: (lock: WebLock | null) => Promise<void> | void,
  ): Promise<void>;
}>;

export type OwnershipStorage = Readonly<{
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}>;

export type OwnershipStorageEvent = Readonly<{
  key: string | null;
  newValue: string | null;
}>;

export type OwnershipBroadcastChannel = {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: Readonly<{ data: unknown }>) => void) | null;
};

export type MatchOwnershipEnvironment = Readonly<{
  locks?: WebLockManager;
  storage?: OwnershipStorage;
  createBroadcastChannel?: (name: string) => OwnershipBroadcastChannel;
  addStorageListener?: (listener: (event: OwnershipStorageEvent) => void) => void;
  removeStorageListener?: (listener: (event: OwnershipStorageEvent) => void) => void;
  setTimeout?: (callback: () => void, delayMs: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}>;

export type MatchOwnershipClock = Readonly<{
  now(): number;
}>;

export type MatchOwnershipOptions = Readonly<{
  environment?: MatchOwnershipEnvironment;
  clock?: MatchOwnershipClock;
  createId?: () => string;
  lockName?: string;
  channelName?: string;
  storageKey?: string;
  takeoverTimeoutMs?: number;
}>;

type OwnershipSignalKind = "claim" | "release" | "takeover";

type OwnershipSignal = Readonly<{
  protocol: "passo-match-ownership-v1";
  kind: OwnershipSignalKind;
  senderId: string;
  sequence: number;
  claimId: string | null;
  takeoverId: string | null;
}>;

type StoredOwnership = Readonly<{
  protocol: "passo-match-ownership-v1";
  ownerId: string;
  claimId: string;
  sequence: number;
  acquiredAt: number;
}>;

const PROTOCOL = "passo-match-ownership-v1" as const;
const DEFAULT_LOCK_NAME = "passo-match-owner";
const DEFAULT_CHANNEL_NAME = "passo-match-owner";
const DEFAULT_STORAGE_KEY = "passo-match-owner-v1";
const DEFAULT_TAKEOVER_TIMEOUT_MS = 250;

const systemClock: MatchOwnershipClock = Object.freeze({ now: () => Date.now() });

const fallbackId = (): string =>
  `owner-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function defaultEnvironment(): MatchOwnershipEnvironment {
  const browserWindow: Window | undefined = typeof window === "undefined" ? undefined : window;
  const browserLocks: LockManager | undefined =
    typeof navigator === "undefined" ? undefined : navigator.locks;
  const BrowserBroadcastChannel: (new (name: string) => BroadcastChannel) | undefined =
    typeof BroadcastChannel === "undefined" ? undefined : BroadcastChannel;
  let storage: OwnershipStorage | undefined;

  try {
    storage = browserWindow?.localStorage;
  } catch {
    // Access can throw in private/blocked browser contexts. Web Locks may still be usable.
  }

  return {
    ...(browserLocks === undefined ? {} : { locks: browserLocks }),
    ...(storage === undefined ? {} : { storage }),
    ...(BrowserBroadcastChannel === undefined
      ? {}
      : {
          createBroadcastChannel: (name: string) =>
            new BrowserBroadcastChannel(name) as unknown as OwnershipBroadcastChannel,
        }),
    ...(browserWindow === undefined
      ? {}
      : {
          addStorageListener: (listener: (event: OwnershipStorageEvent) => void) =>
            browserWindow.addEventListener("storage", listener as unknown as EventListener),
          removeStorageListener: (listener: (event: OwnershipStorageEvent) => void) =>
            browserWindow.removeEventListener("storage", listener as unknown as EventListener),
        }),
    setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
    clearTimeout: (handle) => globalThis.clearTimeout(handle as number),
  };
}

function frozenSnapshot(
  kind: MatchOwnershipSnapshot["kind"],
  reason: MatchOwnershipReason,
  ownerId: string | null,
): MatchOwnershipSnapshot {
  return Object.freeze({ kind, reason, ownerId });
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isSequence(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function parseSignal(value: unknown): OwnershipSignal | null {
  if (value === null || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (
    candidate["protocol"] !== PROTOCOL ||
    (candidate["kind"] !== "claim" &&
      candidate["kind"] !== "release" &&
      candidate["kind"] !== "takeover") ||
    !isNonEmptyString(candidate["senderId"]) ||
    !isSequence(candidate["sequence"]) ||
    (candidate["claimId"] !== null && !isNonEmptyString(candidate["claimId"])) ||
    (candidate["takeoverId"] !== null && !isNonEmptyString(candidate["takeoverId"]))
  ) {
    return null;
  }
  return candidate as OwnershipSignal;
}

function parseStoredOwnership(value: string | null): StoredOwnership | null {
  if (value === null) return null;
  try {
    const candidate: unknown = JSON.parse(value);
    if (candidate === null || typeof candidate !== "object") return null;
    const record = candidate as Record<string, unknown>;
    if (
      record["protocol"] !== PROTOCOL ||
      !isNonEmptyString(record["ownerId"]) ||
      !isNonEmptyString(record["claimId"]) ||
      !isSequence(record["sequence"]) ||
      typeof record["acquiredAt"] !== "number" ||
      !Number.isFinite(record["acquiredAt"])
    ) {
      return null;
    }
    return record as StoredOwnership;
  } catch {
    return null;
  }
}

/**
 * Creates the browser ownership coordinator. The injected environment makes the protocol fully
 * deterministic in tests and avoids a DOM dependency in non-browser callers.
 */
export function createMatchOwnershipPort(options: MatchOwnershipOptions = {}): MatchOwnershipPort {
  return Object.freeze(new MatchOwnershipCoordinator(options));
}

class MatchOwnershipCoordinator implements MatchOwnershipPort {
  readonly #environment: MatchOwnershipEnvironment;
  readonly #clock: MatchOwnershipClock;
  readonly #createId: () => string;
  readonly #lockName: string;
  readonly #storageKey: string;
  readonly #takeoverTimeoutMs: number;
  readonly #listeners = new Set<(snapshot: MatchOwnershipSnapshot) => void>();
  readonly #lastSequenceBySender = new Map<string, number>();
  readonly #takeoverWaiters = new Map<string, () => void>();
  readonly #storageListener = (event: OwnershipStorageEvent): void => this.#onStorage(event);
  readonly #ownerId: string;
  #channel: OwnershipBroadcastChannel | null = null;
  #snapshot: MatchOwnershipSnapshot = frozenSnapshot("read-only", "available", null);
  #claimId: string | null = null;
  #sequence = 0;
  #lockRelease: (() => void) | null = null;
  #lockRequest: Promise<void> | null = null;
  #disposed = false;
  #operation: Promise<MatchOwnershipSnapshot> | null = null;

  constructor(options: MatchOwnershipOptions) {
    this.#environment = options.environment ?? defaultEnvironment();
    this.#clock = options.clock ?? systemClock;
    this.#createId = options.createId ?? fallbackId;
    this.#ownerId = this.#createId();
    this.#lockName = options.lockName ?? DEFAULT_LOCK_NAME;
    this.#storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    this.#takeoverTimeoutMs = options.takeoverTimeoutMs ?? DEFAULT_TAKEOVER_TIMEOUT_MS;

    if (
      this.#environment.addStorageListener !== undefined &&
      this.#environment.removeStorageListener !== undefined
    ) {
      this.#environment.addStorageListener(this.#storageListener);
    }
    if (this.#environment.createBroadcastChannel !== undefined) {
      try {
        this.#channel = this.#environment.createBroadcastChannel(
          options.channelName ?? DEFAULT_CHANNEL_NAME,
        );
        this.#channel.onmessage = (event) => this.#onSignal(event.data);
      } catch {
        // Storage and Web Locks remain valid coordination mechanisms.
      }
    }
  }

  getSnapshot = (): MatchOwnershipSnapshot => this.#snapshot;

  subscribe = (listener: (snapshot: MatchOwnershipSnapshot) => void): (() => void) => {
    if (this.#disposed) return () => undefined;
    this.#listeners.add(listener);
    let subscribed = true;
    return () => {
      if (!subscribed) return;
      subscribed = false;
      this.#listeners.delete(listener);
    };
  };

  acquire = (): Promise<MatchOwnershipSnapshot> => this.#runOperation(false);

  takeOver = (): Promise<MatchOwnershipSnapshot> => this.#runOperation(true);

  release = async (): Promise<MatchOwnershipSnapshot> => {
    if (this.#disposed || this.#snapshot.kind !== "owner") return this.#snapshot;
    await this.#relinquish("available");
    return this.#snapshot;
  };

  dispose = async (): Promise<void> => {
    if (this.#disposed) return;
    this.#disposed = true;
    if (this.#snapshot.kind === "owner") await this.#relinquish("disposed");
    else this.#setSnapshot("read-only", "disposed", null);

    if (this.#environment.removeStorageListener !== undefined) {
      this.#environment.removeStorageListener(this.#storageListener);
    }
    if (this.#channel !== null) {
      this.#channel.onmessage = null;
      this.#channel.close();
      this.#channel = null;
    }
    this.#listeners.clear();
  };

  #runOperation(takeover: boolean): Promise<MatchOwnershipSnapshot> {
    if (this.#disposed) return Promise.resolve(this.#snapshot);
    if (this.#snapshot.kind === "owner") return Promise.resolve(this.#snapshot);
    if (this.#operation !== null) return this.#operation;
    this.#operation = this.#acquire(takeover).finally(() => {
      this.#operation = null;
    });
    return this.#operation;
  }

  async #acquire(takeover: boolean): Promise<MatchOwnershipSnapshot> {
    if (takeover) await this.#requestTakeover();
    if (this.#disposed) return this.#snapshot;

    if (this.#environment.locks !== undefined) {
      const acquired = await this.#acquireLock();
      if (!acquired) return this.#snapshot;

      const existing = this.#readStoredOwnership();
      if (existing === undefined && this.#environment.storage !== undefined) {
        this.#setSnapshot("read-only", "uncertain", null);
        await this.#releaseLock();
        return this.#snapshot;
      }
      // The exclusive lock proves an older record belongs to a terminated document.
      // Replace that advisory signal so an ordinary reload does not require takeover.
      return this.#claimWithLock(takeover ? existing : null);
    }

    return this.#claimWithStorage(takeover);
  }

  async #acquireLock(): Promise<boolean> {
    const locks = this.#environment.locks;
    if (locks === undefined) return false;
    let resolveEntered: ((held: boolean) => void) | null = null;
    const entered = new Promise<boolean>((resolve) => {
      resolveEntered = resolve;
    });
    const resolve = (held: boolean): void => {
      resolveEntered?.(held);
      resolveEntered = null;
    };

    try {
      const request = locks.request(
        this.#lockName,
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (lock === null || this.#disposed) {
            resolve(false);
            return;
          }
          let unlock: (() => void) | null = null;
          const released = new Promise<void>((release) => {
            unlock = release;
          });
          this.#lockRelease = () => unlock?.();
          resolve(true);
          await released;
        },
      );
      this.#lockRequest = request.catch(() => undefined);
      void this.#lockRequest.then(() => resolve(false));
    } catch {
      this.#setSnapshot("read-only", "uncertain", null);
      resolve(false);
    }

    const held = await entered;
    if (!held && !this.#disposed && this.#snapshot.reason === "available") {
      const existing = this.#readStoredOwnership();
      this.#setSnapshot(
        "read-only",
        existing?.ownerId === undefined ? "uncertain" : "owned-by-another",
        existing?.ownerId ?? null,
      );
    }
    return held;
  }

  async #claimWithLock(
    existing: StoredOwnership | null | undefined,
  ): Promise<MatchOwnershipSnapshot> {
    this.#claimId = this.#createId();
    const record: StoredOwnership = Object.freeze({
      protocol: PROTOCOL,
      ownerId: this.#ownerId,
      claimId: this.#claimId,
      sequence: this.#nextSequence(),
      acquiredAt: this.#clock.now(),
    });
    if (
      existing !== undefined &&
      this.#environment.storage !== undefined &&
      !this.#writeAndVerify(record)
    ) {
      this.#claimId = null;
      this.#setSnapshot("read-only", "uncertain", null);
      await this.#releaseLock();
      return this.#snapshot;
    }
    this.#becomeOwner(record);
    return this.#snapshot;
  }

  #claimWithStorage(takeover: boolean): MatchOwnershipSnapshot {
    const existing = this.#readStoredOwnership();
    if (existing === undefined) {
      this.#setSnapshot("read-only", "unavailable", null);
      return this.#snapshot;
    }
    if (existing !== null && existing.ownerId !== this.#ownerId && !takeover) {
      this.#setSnapshot("read-only", "owned-by-another", existing.ownerId);
      return this.#snapshot;
    }

    this.#claimId = this.#createId();
    const record: StoredOwnership = Object.freeze({
      protocol: PROTOCOL,
      ownerId: this.#ownerId,
      claimId: this.#claimId,
      sequence: this.#nextSequence(),
      acquiredAt: this.#clock.now(),
    });
    if (!this.#writeAndVerify(record)) {
      this.#claimId = null;
      this.#setSnapshot("read-only", "uncertain", null);
      return this.#snapshot;
    }
    this.#becomeOwner(record);
    return this.#snapshot;
  }

  async #requestTakeover(): Promise<void> {
    if (this.#snapshot.kind === "owner" || this.#disposed) return;
    const takeoverId = this.#createId();
    if (this.#environment.locks === undefined) {
      // The verified replacement record is the storage-only handover point. Waiting for a peer is
      // unsafe: a crashed or storage-event-starved peer must not make explicit takeover hang.
      this.#publish("takeover", null, takeoverId);
      return;
    }
    const waitForRelease = this.#waitForTakeoverRelease(takeoverId);
    this.#publish("takeover", null, takeoverId);
    await waitForRelease;
  }

  #waitForTakeoverRelease(takeoverId: string): Promise<void> {
    const schedule = this.#environment.setTimeout;
    const clear = this.#environment.clearTimeout;
    if (schedule === undefined || clear === undefined) return Promise.resolve();
    return new Promise((resolve) => {
      const timeout = schedule(() => {
        this.#takeoverWaiters.delete(takeoverId);
        resolve();
      }, this.#takeoverTimeoutMs);
      this.#takeoverWaiters.set(takeoverId, () => {
        clear(timeout);
        this.#takeoverWaiters.delete(takeoverId);
        resolve();
      });
    });
  }

  #becomeOwner(record: StoredOwnership): void {
    this.#setSnapshot("owner", "available", this.#ownerId);
    this.#publish("claim", record.claimId, null, record.sequence);
  }

  async #relinquish(reason: MatchOwnershipReason, takeoverId: string | null = null): Promise<void> {
    const claimId = this.#claimId;
    this.#claimId = null;
    this.#setSnapshot("read-only", reason, null);
    this.#clearOwnStoredOwnership(claimId);
    await this.#releaseLock();
    this.#publish("release", claimId, takeoverId);
  }

  async #releaseLock(): Promise<void> {
    const release = this.#lockRelease;
    this.#lockRelease = null;
    release?.();
    const request = this.#lockRequest;
    this.#lockRequest = null;
    await request;
  }

  #readStoredOwnership(): StoredOwnership | null | undefined {
    const storage = this.#environment.storage;
    if (storage === undefined) return undefined;
    try {
      const raw = storage.getItem(this.#storageKey);
      if (raw === null) return null;
      const record = parseStoredOwnership(raw);
      return record ?? undefined;
    } catch {
      return undefined;
    }
  }

  #writeAndVerify(record: StoredOwnership): boolean {
    const storage = this.#environment.storage;
    if (storage === undefined) return false;
    const value = JSON.stringify(record);
    try {
      storage.setItem(this.#storageKey, value);
      return storage.getItem(this.#storageKey) === value;
    } catch {
      return false;
    }
  }

  #clearOwnStoredOwnership(claimId: string | null): void {
    if (claimId === null) return;
    const storage = this.#environment.storage;
    if (storage === undefined) return;
    try {
      const record = parseStoredOwnership(storage.getItem(this.#storageKey));
      if (record?.ownerId === this.#ownerId && record.claimId === claimId) {
        storage.removeItem(this.#storageKey);
      }
    } catch {
      // A failed best-effort signal cannot make this context retain mutation authority.
    }
  }

  #onStorage(event: OwnershipStorageEvent): void {
    if (this.#disposed || event.key !== this.#storageKey) return;
    const record = parseStoredOwnership(event.newValue);
    if (event.newValue !== null && record === null) {
      if (this.#snapshot.kind === "owner") void this.#relinquish("uncertain");
      else this.#setSnapshot("read-only", "uncertain", null);
      return;
    }
    if (record === null) {
      // With Web Locks, wait for the explicit release acknowledgement: removal precedes physical
      // lock release. In the storage-only fallback, removal itself is the handover acknowledgement.
      if (this.#environment.locks === undefined) this.#resolveTakeoverWaiters();
      if (this.#snapshot.kind !== "owner") this.#setSnapshot("read-only", "available", null);
      return;
    }
    if (record.ownerId === this.#ownerId) return;
    const previous = this.#lastSequenceBySender.get(record.ownerId);
    if (previous !== undefined && record.sequence <= previous) return;
    this.#lastSequenceBySender.set(record.ownerId, record.sequence);
    if (this.#snapshot.kind === "owner") void this.#relinquish("owned-by-another");
    else this.#setSnapshot("read-only", "owned-by-another", record.ownerId);
  }

  #onSignal(value: unknown): void {
    if (this.#disposed) return;
    const signal = parseSignal(value);
    if (signal === null || signal.senderId === this.#ownerId) return;
    const previous = this.#lastSequenceBySender.get(signal.senderId);
    if (previous !== undefined && signal.sequence <= previous) return;
    this.#lastSequenceBySender.set(signal.senderId, signal.sequence);

    if (signal.kind === "takeover") {
      if (this.#snapshot.kind === "owner") void this.#relinquish("taken-over", signal.takeoverId);
      return;
    }
    if (signal.kind === "release") {
      if (signal.takeoverId !== null) this.#resolveTakeoverWaiter(signal.takeoverId);
      if (this.#snapshot.kind !== "owner" && this.#snapshot.ownerId === signal.senderId) {
        this.#setSnapshot("read-only", "available", null);
      }
      return;
    }
    if (this.#snapshot.kind === "owner") void this.#relinquish("owned-by-another");
    else this.#setSnapshot("read-only", "owned-by-another", signal.senderId);
  }

  #publish(
    kind: OwnershipSignalKind,
    claimId: string | null,
    takeoverId: string | null,
    sequence = this.#nextSequence(),
  ): void {
    if (this.#channel === null) return;
    const signal: OwnershipSignal = Object.freeze({
      protocol: PROTOCOL,
      kind,
      senderId: this.#ownerId,
      sequence,
      claimId,
      takeoverId,
    });
    try {
      this.#channel.postMessage(signal);
    } catch {
      // The storage record or Web Lock remains the ownership authority.
    }
  }

  #nextSequence(): number {
    this.#sequence += 1;
    return this.#sequence;
  }

  #resolveTakeoverWaiter(takeoverId: string): void {
    this.#takeoverWaiters.get(takeoverId)?.();
  }

  #resolveTakeoverWaiters(): void {
    for (const resolve of this.#takeoverWaiters.values()) resolve();
  }

  #setSnapshot(
    kind: MatchOwnershipSnapshot["kind"],
    reason: MatchOwnershipReason,
    ownerId: string | null,
  ): void {
    const next = frozenSnapshot(kind, reason, ownerId);
    if (
      this.#snapshot.kind === next.kind &&
      this.#snapshot.reason === next.reason &&
      this.#snapshot.ownerId === next.ownerId
    ) {
      return;
    }
    this.#snapshot = next;
    for (const listener of this.#listeners) listener(next);
  }
}
